import React, { useEffect, useState } from "react";
import { TextField, Box, Typography, Button, IconButton } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import "./SetupMpn.scss";
import {
  useLazyCreateMpinQuery,
  useLazyForgetMpinQuery,
} from "../../Feature_Login/Login_Api";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../redux-store/CustomReduxHooks";
import {
  updateLoginData,
  updateScreenTag,
} from "../../Feature_Login/Login_Slice";
import { updateLoadingState } from "../../Feature_Loading/Loading_Slice";
import CustomNetworkImage from "../../../components/CustomNetworkImage/CustomNetworkImage";
import ABCLogo from "../../../assets/images/ABC-Service.jpg";
import CustomThreeDotsLoader from "../../../components/CustomThreeDotsLoader/CustomThreeDotsLoader";
import { useLocation, useNavigate } from "react-router-dom";
import {
  generateDeviceID,
  getMobileOperatingSystem,
} from "../../../utils/helper";
import CsLocalStorageUtils from "../../../utils/CsLocalStorageUtils";
import { setError } from "../../../redux-store/reducers/CsErrorSlice";
import { triggerCleverTapEvent } from "../../../utils/CleverTap";
import { CleverTapEventType } from "../../../utils/CleverTapConstant";
import { CT_ScreenEvntDtls } from "../../Feature_CleverTap/ScreenEvents/CleverTapEventDetails";
import { GTAScreenType, SCREEN_TYPE } from "../../../utils/constants";
import TriggerGTAEvent from "../../../utils/GTA";
import { Colors } from "../../../styles/constant";
import CustomOnePlatformBox from "../../../components/CustomOnePlatformBox/CustomOnePlatformBox";
import useWindowSize from "../../../hooks/useWindowSize";
import AlertRhombus from "../../../assets/icons/alert-rhombus.svg";
import { LoginScreenValues } from "../../../globalTypes/GlobalTypes";
import CsSessionStorageUtils from "../../../utils/CsSessionStorageUtils";
import AuthBootstrapBridge from "../../../routes/Guards/AuthBootstrapBridge";
interface MpinInputProps {
  length?: number;
}

const SetupMpn: React.FC<MpinInputProps> = ({ length = 4 }) => {
  const [mpnvisible, setMpnvisible] = useState<boolean>(true);
  const [confirmmpnvisible, setConfirmmpnvisible] = useState<boolean>(true);
  const [mpin, setMpin] = useState<string[]>(Array(length).fill(""));
  const [confirmMpin, setConfirmMpin] = useState<string[]>(
    Array(length).fill(""),
  );
  const [authGuardEnabled, initAuthGuardChecks] = useState(false);

  const [isMatched, setIsMatched] = useState<boolean | null>(null);
  const [isWeakMpin, setIsWeakMpin] = useState(false);
  const [weakMpinMessage, setWeakMpinMessage] = useState<string | null>(null);
  const appDispatch = useAppDispatch();
  const [createMpinApi] = useLazyCreateMpinQuery();
  const [forgetMpin] = useLazyForgetMpinQuery();
  const csUserId = useAppSelector((state) => state.CsAppSlice?.csUserId);
  const loginSlice = useAppSelector((state) => state.LoginSlice);

  const cmsData = useAppSelector((state) => state.CmsSlice.CmsPageVariableData);
  const cmsLogo =
    cmsData.find((i) => i.attributes.cmsType === "ABC Logo")?.attributes.image
      .data?.attributes.url ?? "";
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const { state } = useLocation();
  const { width } = useWindowSize();

  useEffect(() => {
    triggerCleverTapEvent(CleverTapEventType.SCREEN_EVENT, {
      ScreenName: CT_ScreenEvntDtls.R_GAE.gaLoginSetMpinDetails.screenName,
      ScreenType: SCREEN_TYPE.LoginScreenType,
    });
    appDispatch(updateLoadingState(false));
  }, []);

  useEffect(() => {
    const enteredMpin = mpin.join("");
    const enteredConfirmMpin = confirmMpin.join("");
    if (enteredMpin.length === length) {
      // Rule 1: Identical digits
      const digitCounts: Record<string, number> = {};
      for (let digit of enteredMpin) {
        digitCounts[digit] = (digitCounts[digit] || 0) + 1;
      }
      const hasRepeats = Object.values(digitCounts).some((count) => count >= 2);

      // Rule 2: Sequential digits
      const isSequential =
        "0123456789".includes(enteredMpin) ||
        "9876543210".includes(enteredMpin);

      if (isSequential) {
        setWeakMpinMessage(
          "That looks like a sequence! Pick something less obvious.",
        );
        setIsWeakMpin(true);
      } else if (hasRepeats) {
        setWeakMpinMessage("Identical digits make mPIN too easy to guess");
        setIsWeakMpin(true);
      } else {
        setWeakMpinMessage(null);
        setIsWeakMpin(false);
      }
    } else {
      setWeakMpinMessage(null);
      setIsWeakMpin(false);
    }

    if (enteredMpin.length === length && enteredConfirmMpin.length === length) {
      if (enteredMpin !== enteredConfirmMpin) {
        setIsMatched(false);
      } else {
        setIsMatched(true);
      }
    } else {
      setIsMatched(null);
    }
  }, [mpin, confirmMpin, length]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
    setField: React.Dispatch<React.SetStateAction<string[]>>,
    field: string[],
  ) => {
    const value = e.target.value;
    if (value.match(/^\d$/)) {
      const newField = [...field];
      newField[index] = value;
      setField(newField);

      if (index < length - 1) {
        const nextField = document.getElementById(
          `${setField === setMpin ? "mpin-input" : "confirm-mpin-input"}-${
            index + 1
          }`,
        );
        if (nextField) (nextField as HTMLInputElement).focus();
      }
    }
  };

  const handleKeyDown = (
    e: any,
    index: number,
    setField: React.Dispatch<React.SetStateAction<string[]>>,
    field: string[],
  ) => {
    if (e.key === "Backspace") {
      if (field[index]) {
        const newField = [...field];
        newField[index] = "";
        setField(newField);
      } else if (index > 0) {
        const prevField = document.getElementById(
          `${setField === setMpin ? "mpin-input" : "confirm-mpin-input"}-${
            index - 1
          }`,
        );
        if (prevField) (prevField as HTMLInputElement).focus();
      }
    }
    if (e.key === "Enter") {
      e.preventDefault(); // Prevent default behavior
      handleConfirm(); // Call your confirm function
    }
  };

  const handleClickShowPasswordMpn = () => {
    setMpnvisible(!mpnvisible);
  };

  const handleClickShowPasswordconfirmMpn = () => {
    setConfirmmpnvisible(!confirmmpnvisible);
  };

  const handleCleverTapCTAClick = () => {
    triggerCleverTapEvent("serv_setup_mpin_cta_click", {
      ScreenName: "Servicing Personal Details Authentication Successful Screen",
      ScreenType: SCREEN_TYPE.generalAppType,
      isPrevPageNameRequired: true,
      ctaText: "Continue",
    });
  };

  const handleConfirm = () => {
    TriggerGTAEvent("serv_setup_mpin_cta_click", {
      screen_type: GTAScreenType.LoginScreenType,
      screen_name: "serv_login_setup_mpin_details_enter_screen",
      CTA_text: "continue",
      isPrevPageNameRequired: true,
    });
    handleCleverTapCTAClick();
    setIsMatched(null);
    if (mpin.join("") !== confirmMpin.join("")) {
      setIsMatched(false);
      return;
    }

    const saveMpinNGotoHome = () => {
      appDispatch(updateLoginData({ key: "mpin", value: mpin.join("") }));
      CsLocalStorageUtils.set("MPIN_FOR_MOBILE", loginSlice.mobilenumber);
      appDispatch(updateScreenTag(LoginScreenValues.LoginWithMPIN));
      CsSessionStorageUtils.set("LOGGED_IN_TIME", new Date().toISOString());
      appDispatch(updateLoginData({ key: "isUserLoggedIn", value: true }));
      initAuthGuardChecks(true);
    };

    const showTechError = (err?: Error) => {
      appDispatch(
        setError({
          error: err || new Error("Error"),
          message: err?.message || "Change Mpin API failed",
        }),
      );
    };

    if (state?.type === "forgetMpin") {
      if (mpin.join("") === confirmMpin.join("")) {
        setLoading(true);
        forgetMpin({
          mpin: mpin.join(""),
          csUserId: csUserId || "",
          mobileNo: loginSlice.mobilenumber || "",
          deviceType: getMobileOperatingSystem(),
          deviceId: generateDeviceID(),
        })
          .then((response) => {
            if (response.data?.code === 1) {
              saveMpinNGotoHome();
              setIsMatched(true);
            } else {
              showTechError();
            }
          })
          .catch((error) => {
            showTechError(error);
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        setIsMatched(false);
      }
    } else {
      if (mpin.join("") === confirmMpin.join("")) {
        setLoading(true);
        createMpinApi({
          mpin: mpin.join(""),
          csUserId: csUserId || "",
          mobileNo: loginSlice.mobilenumber || "",
          deviceType: getMobileOperatingSystem(),
          deviceId: generateDeviceID(),
        })
          .then((response) => {
            if (response.data?.code === 1) {
              saveMpinNGotoHome();
              setIsMatched(true);
            } else {
              showTechError();
            }
          })
          .catch((error) => {
            showTechError();
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        setIsMatched(false);
      }
    }
  };

  const gotoNextStep = (status: boolean) => {
    if (!status) {
      return;
    }
    navigate("/", { replace: true });
  };

  const logoImageStyle = {
    height: "2.75rem",
    width: "7.125rem",
  };

  return (
    <Box className="setup-mpn-wrapper">
      <Box className="header-section">
        <CustomNetworkImage
          src={cmsLogo || ABCLogo}
          alt="abc-logo-img"
          style={logoImageStyle}
        />
      </Box>
      {<CustomOnePlatformBox />}

      <Box
        className="cardContentLayout"
        paddingBottom={
          getMobileOperatingSystem() === "iOS" ? "5rem !important" : "2rem"
        }
      >
        <Box className="inside-layout-wrapper">
          <Typography
            fontWeight={700}
            fontSize={24}
            marginBottom="0.5rem"
            lineHeight={"1.8rem"}
          >
            Set-up mPIN
          </Typography>
          <Typography variant="body1" color={Colors.descriptionGray}>
            Set mPIN for making login easy and secure
          </Typography>
          <Typography variant="h5" marginTop="1rem" marginBottom=".375rem">
            Set your 4 digit mPIN
          </Typography>
          <Box display="flex" justifyContent="space-between">
            <Box display="flex" justifyContent="space-between" width="66%">
              {mpin.map((value, index) => (
                <TextField
                  key={`mpin-${index}`}
                  id={`mpin-input-${index}`}
                  // value={value}
                  value={mpnvisible && value ? "*" : value}
                  type="text"
                  onChange={(e) => handleChange(e, index, setMpin, mpin)}
                  onKeyDown={(e) => handleKeyDown(e, index, setMpin, mpin)}
                  inputProps={{
                    maxLength: 1,
                    style: {
                      textAlign: "center",
                      fontSize: "1rem",
                      fontWeight: "bold",
                      color: isWeakMpin ? Colors.red : Colors.black,
                    },
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                  className="mpn-textfield"
                />
              ))}
            </Box>
            <IconButton
              aria-label="toggle password visibility"
              onClick={handleClickShowPasswordMpn}
            >
              {mpnvisible ? (
                <VisibilityOff
                  style={{ fontSize: "1rem", color: Colors.red }}
                />
              ) : (
                <Visibility style={{ fontSize: "1rem", color: Colors.red }} />
              )}
            </IconButton>
          </Box>

          <Typography variant="h5" marginTop="1rem" marginBottom=".375rem">
            Confirm mPIN
          </Typography>
          <Box display="flex" justifyContent="space-between">
            <Box display="flex" justifyContent="space-between" width="66%">
              {confirmMpin.map((value, index) => (
                <TextField
                  key={`confirm-mpin-${index}`}
                  id={`confirm-mpin-input-${index}`}
                  value={confirmmpnvisible && value ? "*" : value}
                  type="text"
                  onChange={(e) =>
                    handleChange(e, index, setConfirmMpin, confirmMpin)
                  }
                  onKeyDown={(e) =>
                    handleKeyDown(e, index, setConfirmMpin, confirmMpin)
                  }
                  inputProps={{
                    maxLength: 1,
                    style: {
                      textAlign: "center",
                      fontSize: "1rem",
                      fontWeight: "bold",
                      color: isWeakMpin ? Colors.red : Colors.black,
                    },
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                  className="mpn-textfield"
                />
              ))}
            </Box>
            <IconButton
              aria-label="toggle password visibility"
              onClick={handleClickShowPasswordconfirmMpn}
            >
              {confirmmpnvisible ? (
                <VisibilityOff
                  style={{ fontSize: "1rem", color: Colors.red }}
                />
              ) : (
                <Visibility style={{ fontSize: "1rem", color: Colors.red }} />
              )}
            </IconButton>
          </Box>

          {isMatched === false && (
            <Box display="flex" alignItems="center" marginTop="1rem">
              <img
                src={AlertRhombus}
                alt="incorrect-otp"
                style={{ marginRight: "0.5rem" }}
              />
              <Typography marginY={"0.5rem"} color={Colors.red} variant="h6">
                {"Entered mPINs do not match"}
              </Typography>
            </Box>
          )}
          {weakMpinMessage && (
            <Box display="flex" alignItems="center" marginTop="0.5rem">
              <img
                src={AlertRhombus}
                alt="weak-mpin"
                style={{ marginRight: "0.5rem" }}
              />
              <Typography marginY={"0.5rem"} color={Colors.red} variant="h6">
                {weakMpinMessage}
              </Typography>
            </Box>
          )}
          {/* {isMatched === true && (
            <Typography color="primary">MPIN set successfully!</Typography>
          )} */}

          <Box
            display="flex"
            justifyContent="center"
            marginTop="1.25rem"
            // marginBottom={{ xs: "1.875rem", sm: 0 }}
          >
            {loading ? (
              <CustomThreeDotsLoader />
            ) : (
              <Button
                variant="contained"
                onClick={handleConfirm}
                disabled={
                  mpin.some((digit) => digit === "") ||
                  confirmMpin.some((digit) => digit === "") ||
                  (isMatched === false &&
                    mpin.join("") !== confirmMpin.join(""))
                }
                sx={{
                  width: "100%",
                  height: "3rem",
                  borderRadius: "1.25rem",
                  // marginBottom: "2.125rem",
                  "&:hover": {
                    backgroundColor: "rgba(175, 179, 192, 1)",
                  },
                }}
              >
                Continue
              </Button>
            )}
          </Box>
        </Box>
      </Box>
      {authGuardEnabled && <AuthBootstrapBridge onResult={gotoNextStep} />}
    </Box>
  );
};

export default SetupMpn;
